import * as React from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { equals } from 'ramda';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDRadioGroup from '../../../../../../../BaseComponents/RadioGroup/EDRadioGroup';
import { IColumn } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EkonomiskAnalysChart from '../EkonomiskAnalysChart/EkonomiskAnalysChart';
import EDGrid from '../../../../../../../BaseComponents/GridComponents/EDGrid';
import EDFormGroup from '../../../../../../../BaseComponents/FormGroup/EDFormGroup';
import { ChartType } from '../../Queries/EkonomiskAnalysChartQuery/EkonomiskAnalysChartQuery';
import {
  combineUrval,
  getUrvalText,
  NO_URVAL_TEXT,
} from '../SideMenu/Components/UrvalOchSummering/Components/Urval/Utils/UrvalUtils';
import { EkonomiskAnalysContextConsumer } from '../Context/EkonomiskAnalysContext';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import EDStandardSelect from '../../../../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import { Chart } from 'highcharts';
import EkonomiskAnalysPrintChartPDFModal from './Components/EkonomiskAnalysPrintChartPDFModal/EkonomiskAnalysPrintChartPDFModal';
import EkonomiskAnalysExportToolbar from '../EkonomiskAnalysExportToolbar/EkonomiskAnalysExportToolbar';
import FunctionMenu from '../../../../../_Common/Components/FunctionMenu/FunctionMenu/FunctionMenu';
import FunctionItem from '../../../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import EkonomiskAnalysColumnCaptionQuery from '../../Queries/EkonomiskAnalysColumnCaptionQuery/EkonomiskAnalysColumnCaptionQuery';
import { preloadDDINFont } from '../../Utils/chartUtils';
import { IsPeriodLockedFnType } from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import EDCollapse from '../../../../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';

export interface IAnalysChartSettings {
  id: string;
  kodbehorighetId: string;
  grundurvalId?: string;
  analys: IFilledLocalAnalys;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  availableColumns: IColumn[];
  chartSettings: IAnalysChartSettings;
  denomination: number;
  nrDecimals: number;
  isPeriodLocked: IsPeriodLockedFnType;
  classes?: any;
  forceHelarsjamforelse: boolean;
}

interface IState {
  chartType: ChartType;
  selectedColumnChartColumns: number[];
  selectedPieChartColumn: number;
  grafColumns: number[];
  chart: Chart | undefined;
  printChartPDFModalOpen: boolean;
  open: boolean;
}

const styles = (theme: Theme) => ({
  radioButtonContainer: {
    margin: 0,
  },
  toolbarWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formControlRoot: {
    minWidth: '250px',
  },
  selectAllContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  selectionContainer: {
    marginTop: theme.spacing(1.5),
  },
});

/**
 * En dialog som visar olika grafer och inställningar för dem utifrån en analys.
 */
export class EkonomiskAnalysChartModalBase extends React.Component<
  IProps,
  IState
> {
  constructor(props: IProps) {
    super(props);

    const chartColumns = props.availableColumns.map((column, index) => index);

    this.state = {
      chartType: 'column',
      selectedColumnChartColumns: chartColumns.slice(0),
      selectedPieChartColumn: 0,
      grafColumns: chartColumns.slice(0),
      chart: undefined,
      printChartPDFModalOpen: false,
      open: false,
    };
  }
  componentDidMount() {
    preloadDDINFont();
  }

  componentDidUpdate(prevProps: IProps) {
    if (!equals(prevProps.availableColumns, this.props.availableColumns)) {
      this.reset();
    }
  }

  afterChartCreated = (chart: Chart) => {
    this.setState({ chart });
  };

  chartTypeChange = (type: string) => {
    const chartType = type as ChartType;
    this.setState(prevState => {
      return {
        chartType,
      };
    });
    this.update(chartType);
  };

  selectedColumnCheck = (index: number) => {
    this.setState(prevState => {
      const exists = prevState.selectedColumnChartColumns.some(
        x => x === index
      );

      const selectedColumns = exists
        ? [...prevState.selectedColumnChartColumns.filter(x => x !== index)]
        : [...prevState.selectedColumnChartColumns, index];

      return { selectedColumnChartColumns: selectedColumns };
    });
  };

  toggleAllColumns = () => {
    const columnIndexes = this.props.availableColumns.map<number>((c, i) => i);

    this.setState(prevState => {
      const allSelected =
        prevState.selectedColumnChartColumns.length === columnIndexes.length;

      const selectedColumns = allSelected ? [] : columnIndexes;

      return {
        selectedColumnChartColumns: selectedColumns,
        open: true,
      };
    });
  };

  selectPieColumn = (index: number) => {
    this.setState(
      {
        selectedPieChartColumn: index,
      },
      () => {
        this.update(this.state.chartType);
      }
    );
  };

  update = (type: ChartType) => {
    this.setState(prevState => {
      let grafColumns: number[] = [];
      switch (type) {
        case 'column':
          grafColumns = prevState.selectedColumnChartColumns;
          break;
        case 'pie':
          grafColumns = [prevState.selectedPieChartColumn];
          break;
        case 'line':
          grafColumns = prevState.selectedColumnChartColumns;
          break;
      }
      return { grafColumns: grafColumns };
    });
  };

  reset = () => {
    const chartColumns = this.props.availableColumns.map(
      (column, index) => index
    );

    this.setState({
      chartType: 'column',
      selectedColumnChartColumns: chartColumns.slice(0),
      selectedPieChartColumn: 0,
      grafColumns: chartColumns.slice(0),
    });
  };

  exportChart = () => {
    if (this.state.chart) {
      this.state.chart.exportChartLocal({ type: 'image/png' }, {});
    }
  };

  openPrintChartPDFModal = () => {
    this.setState({ printChartPDFModalOpen: true });
  };

  closePrintChartPDFModal = () => {
    this.setState({ printChartPDFModalOpen: false });
  };

  render() {
    const { chartSettings } = this.props;
    return (
      <>
        <Dialog
          title="Graf"
          open={this.props.open}
          onClose={this.props.onClose}
          fullWidth={true}
          maxWidth={false}
          footerActions={[
            { label: 'Avbryt', onClick: this.props.onClose },
            {
              label: 'Uppdatera',
              onClick: () => this.update(this.state.chartType),
            },
          ]}
          headerTools={<EkonomiskAnalysExportToolbar reset={this.reset} />}
        >
          <div className={this.props.classes.toolbarWrapper}>
            <EDRadioGroup
              label="Typ"
              value={this.state.chartType}
              onChange={this.chartTypeChange}
              radioDefs={[
                { value: 'column', label: 'Stapel' },
                { value: 'pie', label: 'Cirkel' },
                { value: 'line', label: 'Linje' },
              ]}
              classes={{ formControl: this.props.classes.radioButtonContainer }}
              row
            />
            <FunctionMenu id="graphChart">
              <FunctionItem
                disabled={!this.state.chart}
                disabledReason="Ogiltig graf"
                onClick={this.openPrintChartPDFModal}
                iconType="print"
              >
                Skriv ut
              </FunctionItem>
              <FunctionItem
                disabled={!this.state.chart}
                disabledReason="Ogiltig graf"
                onClick={this.exportChart}
                iconType="image"
              >
                Exportera som bild
              </FunctionItem>
            </FunctionMenu>
          </div>
          <div className={this.props.classes.selectionContainer}>
            <EkonomiskAnalysContextConsumer>
              {ekAnalysContext => {
                const egetUrval = ekAnalysContext.urval
                  ? getUrvalText(ekAnalysContext.urval)
                  : NO_URVAL_TEXT;

                return (
                  <>
                    <EDTypography>
                      <EDTypography bold variant="body2" component="span">
                        Eget urval:
                      </EDTypography>{' '}
                      {egetUrval}
                    </EDTypography>

                    <EDTypography>
                      <EDTypography bold variant="body2" component="span">
                        Kodbehörighet:
                      </EDTypography>
                      {ekAnalysContext.kodbehorighetsUrval &&
                      ekAnalysContext.kodbehorighetsUrval.savedUrvalProps
                        ? ` ${ekAnalysContext.kodbehorighetsUrval?.savedUrvalProps.namn}`
                        : 'Kodbehörighet kan inte hittas'}
                    </EDTypography>

                    <EDTypography>
                      <EDTypography bold variant="body2" component="span">
                        Grundurval:
                      </EDTypography>{' '}
                      {ekAnalysContext.grundurvalUrval &&
                      ekAnalysContext.grundurvalUrval.savedUrvalProps
                        ? ` ${ekAnalysContext.grundurvalUrval.savedUrvalProps.namn}`
                        : 'Inget urval gjort'}
                    </EDTypography>
                  </>
                );
              }}
            </EkonomiskAnalysContextConsumer>
          </div>
          <EkonomiskAnalysChart
            id={chartSettings.id}
            dates={chartSettings.analys.dates}
            kodbehorighetId={chartSettings.kodbehorighetId}
            grundurvalId={chartSettings.grundurvalId}
            detailedSelection={
              chartSettings.analys.detaljUrval
                ? chartSettings.analys.urval
                  ? combineUrval(
                      chartSettings.analys.urval,
                      chartSettings.analys.detaljUrval
                    )
                  : chartSettings.analys.detaljUrval
                : chartSettings.analys.urval
            }
            grafColumns={this.state.grafColumns}
            chartType={this.state.chartType}
            analys={chartSettings.analys}
            denomination={this.props.denomination}
            nrDecimals={this.props.nrDecimals}
            afterChartCreated={this.afterChartCreated}
          />
          {(this.state.chartType === 'column' ||
            this.state.chartType === 'line') && (
            <EDFormGroup line size="medium">
              <>
                <EDGrid
                  className={this.props.classes.selectAllContainer}
                  item
                  sm={3}
                >
                  <EDCheckbox
                    checked={this.state.selectedColumnChartColumns.length !== 0}
                    indeterminate={
                      this.state.selectedColumnChartColumns.length !== 0 &&
                      this.state.selectedColumnChartColumns.length !==
                        this.props.availableColumns.length
                    }
                    label="Inkludera alla kolumner"
                    onCheck={() => this.toggleAllColumns()}
                  />
                  <EDIconButton
                    icon={this.state.open ? 'arrow_drop_up' : 'arrow_drop_down'}
                    margin="none"
                    onClick={() => {
                      this.setState(prevState => {
                        return { open: !prevState.open };
                      });
                    }}
                    padding="dense"
                    tooltip="Fäll ut alla val"
                  />
                </EDGrid>

                <EDCollapse open={this.state.open}>
                  <EDGrid container spacing={0}>
                    {this.props.availableColumns.map((column, index) => {
                      const name = column.displayLabel
                        ? column.displayLabel
                        : column.name;
                      return (
                        <EDGrid item key={index} sm={3}>
                          <EDCheckbox
                            checked={this.state.selectedColumnChartColumns.some(
                              x => x === index
                            )}
                            label={name}
                            onCheck={() => this.selectedColumnCheck(index)}
                          />
                        </EDGrid>
                      );
                    })}
                  </EDGrid>
                </EDCollapse>
              </>
            </EDFormGroup>
          )}
          {this.state.chartType === 'pie' && (
            <EDStandardSelect<number>
              label="Välj kolumn att visa som cirkeldiagram"
              value={this.state.selectedPieChartColumn}
              onChange={this.selectPieColumn}
              fullWidth={false}
              items={this.props.availableColumns.map((col, index) => ({
                header: col.displayLabel ? col.displayLabel : col.name,
                value: index,
              }))}
              formControlRoot={this.props.classes.formControlRoot}
            />
          )}
        </Dialog>
        <EkonomiskAnalysPrintChartPDFModal
          open={this.state.printChartPDFModalOpen}
          close={this.closePrintChartPDFModal}
          chart={this.state.chart}
          chartSettings={this.props.chartSettings}
          chartType={this.state.chartType}
          isPeriodLocked={this.props.isPeriodLocked}
          forceHelarsjamforelse={this.props.forceHelarsjamforelse}
        />
      </>
    );
  }
}

const EkonomiskAnalysChartModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const [columns, setColumns] = React.useState(props.availableColumns);

  React.useEffect(() => {
    if (props.open) {
      setColumns(props.availableColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const columnsWithoutDisplayLabel = columns.map(col => ({
    ...col,
    displayLabel: undefined,
  }));
  const propColumnsWithoutDisplayLabel = props.availableColumns.map(col => ({
    ...col,
    displayLabel: undefined,
  }));

  const usedColumns = equals(
    columnsWithoutDisplayLabel,
    propColumnsWithoutDisplayLabel
  )
    ? columns
    : props.availableColumns;

  return (
    <EkonomiskAnalysColumnCaptionQuery
      analysName={props.chartSettings.analys.name}
      codePrivilegeId={props.chartSettings.kodbehorighetId}
      dates={{
        to: { ...props.chartSettings.analys.dates.to, userString: '' },
        from: { ...props.chartSettings.analys.dates.from, userString: '' },
      }}
      columns={usedColumns}
      isInterval={props.chartSettings.analys.isInterval}
      onUpdate={cols => setColumns(cols)}
    >
      {oldColumns => {
        const { availableColumns, ...rest } = props;
        if (oldColumns) {
          return (
            <EkonomiskAnalysChartModalBase
              availableColumns={oldColumns}
              {...rest}
            />
          );
        } else {
          return (
            <EkonomiskAnalysChartModalBase
              availableColumns={columns}
              {...rest}
            />
          );
        }
      }}
    </EkonomiskAnalysColumnCaptionQuery>
  );
};

export default withStyles(EkonomiskAnalysChartModal, styles);
